function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { action, computed, extendObservable, toJS } from 'mobx';
import get from 'lodash/get';
import moment from 'moment';
import Availability, { AVAILABILITY_TYPES } from "./Availability.model";
import { eplApi, scheduleApi, getAddressById, timezoneApi } from '@atheneum/api';
import { doesSlotOverlapWithAnySchedules, groupAvailabilities, sortByDate, isSlotAfterHedgeFundCompianceExpiration, splitInto15MinAvailabilities, getTimezone, getFormattedTimezone } from '@atheneum/api/dist/lib';
import { showDialog, isCancellationError } from "../components/Dialog";

class AvailabilityStore {
  constructor({
    staticData,
    epl,
    project,
    userStore
  }) {
    _defineProperty(this, "init", () => {
      const eplId = this.epl.id;
      const officeId = this.project.officeId;
      const projectId = this.project.id;
      const addressId = this.userStore.user.addressId;
      this.loading = true;
      return Promise.all([getAddressById(addressId), this.loadRequestedSchedules(eplId), this.loadExpertAvailabilities(eplId), this.loadClientAvailabilities(projectId), this.loadClientOfficeTimezone(officeId)]).then(([address]) => {
        this.address = address; // setup schedule entry, if any exists

        const availabilityWithSchedule = this.expertAvailabilities.find(a => a.schedule && a.schedule.canceled === false);

        if (availabilityWithSchedule) {
          const existingSchedule = availabilityWithSchedule.schedule;
          const schedule = Object.assign({}, toJS(existingSchedule), {
            clientAvailabilities: this.clientAvailabilities.filter(a => a.scheduleId === existingSchedule.id),
            expertAvailabilities: this.expertAvailabilities.filter(a => a.scheduleId === existingSchedule.id)
          });
          this.persistedSchedule = schedule;
          this.schedule = schedule;
        }

        const otherSchedules = this.clientAvailabilities.filter(av => get(av, 'schedule.canceled') === false && get(av, 'schedule.id') !== get(this.schedule, 'id')).map(av => av.schedule);
        this.schedulesOnSameProject.replace(otherSchedules);
        this.generateAvailabilityGroups();
        const scheduleId = this.schedule && this.schedule.id;
        return this.loadScheduleInfo(scheduleId);
      }).catch(error => {
        console.error(error);
        this.error = error;
        this.loading = false;
      }).then(() => {
        this.loading = false;
      });
    });

    _defineProperty(this, "loadRequestedSchedules", action(eplId => {
      return scheduleApi.getScheduleRequest(eplId).then(scheduleRequests => {
        this.scheduleRequests.replace(scheduleRequests);
      });
    }));

    _defineProperty(this, "loadExpertAvailabilities", action(eplId => {
      return scheduleApi.getExpertAvailabilities(eplId).then(availabilities => {
        const expertAvailabilities = availabilities.map(a => new Availability(a, this));
        this.expertAvailabilities.replace(expertAvailabilities);
        return this.expertAvailabilities;
      }).catch(error => {
        console.error(error);
        this.error = error;
      });
    }));

    _defineProperty(this, "loadClientAvailabilities", action(projectId => {
      return scheduleApi.getClientAvailabilities(projectId).then(availabilities => {
        const clientAvailabilities = availabilities.map(a => new Availability(a, this));
        this.clientAvailabilities.replace(clientAvailabilities);
        return this.clientAvailabilities;
      }).catch(error => {
        console.error(error);
        this.error = error;
      });
    }));

    _defineProperty(this, "checkForTakenSlots", action((firstAv, lastAv) => {
      const expertId = this.epl.expert.id;
      const eplId = this.epl.id;
      return scheduleApi.getTakenSlots(expertId, firstAv, lastAv, eplId).then(slots => {
        this.takenSlots.replace(this.takenSlots.concat(slots));
      }).catch(error => {
        console.error(error);
        this.error = error;
      });
    }));

    _defineProperty(this, "loadClientOfficeTimezone", action(officeId => {
      return timezoneApi.getOfficeTimezone(officeId).then(timezone => {
        this.officeTimezone = timezone;
      }).catch(error => {
        console.error(error);
        this.error = error;
      });
    }));

    _defineProperty(this, "loadScheduleInfo", async scheduleId => {
      if (!scheduleId) {
        this.persistedScheduleInfo = null;
        return;
      }

      return scheduleApi.getScheduleInfo(scheduleId).then(scheduleInfo => {
        this.persistedScheduleInfo = scheduleInfo;
      }).catch(error => {
        console.error(error);
        this.error = error;
      });
    });

    _defineProperty(this, "generateAvailabilityGroups", action(() => {
      const sortedAvailabilities = sortByDate(this.availabilities, {
        prop: 'startTime'
      });
      const availabilityGroups = groupAvailabilities(sortedAvailabilities);
      this.availabilityGroups = availabilityGroups;
    }));

    _defineProperty(this, "changeTimezone", action(event => {
      this.timezone = parseInt(event.target.value, 10);
    }));

    _defineProperty(this, "changeInterviewDuration", action(event => {
      const newDuration = event.target.value;

      if (this.schedule || this.scheduleRequests.length) {
        return showDialog({
          title: 'Confirm duration change',
          html: '<div>Are you sure you want to change the interview duration?<br /> <strong>Warning:</strong> This will remove the existing interview schedules and client requests</div>',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Change interview duration'
        }).then(result => {
          if (result) {
            this.schedule = null;
            this.overwriteSchedule = true;
            this.interviewDuration = newDuration;
          }
        }).catch(err => {
          if (isCancellationError(err)) return;
          console.error(err);
        });
      }

      this.interviewDuration = newDuration;
    }));

    _defineProperty(this, "changeLockInterviewDuration", newLockValue => {
      this.lockInterviewDuration = newLockValue;
    });

    _defineProperty(this, "removeAvailabilities", action((availabilities, groupKey) => {
      const availabilitiesWithSchedule = this.getAvailabilitiesWithScheduleSlots(availabilities);

      if (availabilitiesWithSchedule.length) {
        throw new Error(`You cannot delete this group, because it's used on a different EPL`);
      }

      const availabilitiesWithoutSchedule = this.getAvailabilitiesWithoutScheduleSlots(availabilities);
      const availabilitiesWithCurrentSchedule = availabilities.filter(availability => availability.schedule && availability.schedule.id && availability.schedule.canceled === false && this.persistedSchedule && availability.schedule.id === this.persistedSchedule.id);
      const containsNonDeletedSchedule = availabilitiesWithCurrentSchedule.length && this.schedule && this.overwriteSchedule === false;

      if (containsNonDeletedSchedule) {
        throw new Error(`You cannot delete this scheduled time slot`);
      }

      if (availabilitiesWithCurrentSchedule.length) {
        this.schedule = null;
        const isScheduleSaved = availabilitiesWithCurrentSchedule.find(availability => availability.schedule && availability.schedule.id);

        if (isScheduleSaved) {
          this.overwriteSchedule = true;
        }
      }

      availabilitiesWithoutSchedule.map(availability => availability.remove());
      availabilitiesWithCurrentSchedule.map(availability => availability.remove());
      const clientAvailabilitiesToRemove = availabilitiesWithoutSchedule.concat(availabilitiesWithCurrentSchedule).filter(a => a.clientAvailabilityId).map(a => a.clientAvailabilityId);
      const expertAvailabilitiesToRemove = availabilitiesWithoutSchedule.concat(availabilitiesWithCurrentSchedule).filter(a => a.expertAvailabilityId).map(a => a.expertAvailabilityId);
      eplApi.saveScheduleSettings(this.epl.id, {
        clientAvailabilitiesToRemove,
        expertAvailabilitiesToRemove
      });
      this.generateAvailabilityGroups();
    }));

    _defineProperty(this, "getAvailabilitiesWithScheduleSlots", availabilities => {
      const availabilitiesWithScheduleSlots = availabilities.filter(availability => {
        const hasAvailabilitySchedule = availability.schedule && availability.schedule.id && availability.schedule.canceled === false;
        if (!hasAvailabilitySchedule) return false;
        const scheduleId = availability.schedule.id;
        const currentScheduleId = this.persistedSchedule && this.persistedSchedule.id;
        if (!currentScheduleId && scheduleId) return true;
        const isScheduleForAnotherEpl = scheduleId !== currentScheduleId;
        if (isScheduleForAnotherEpl) return true;
        return false;
      });
      return availabilitiesWithScheduleSlots;
    });

    _defineProperty(this, "getAvailabilitiesWithoutScheduleSlots", availabilities => {
      const availabilitiesWithoutSchedules = availabilities.filter(availability => {
        return !availability.schedule || !availability.schedule.id || availability.schedule.canceled === true;
      });
      return availabilitiesWithoutSchedules;
    });

    _defineProperty(this, "addAvailability", action(newAvailabilityData => {
      const parentAccountType = this.project.office.account.parentAccount.accountTypeId;
      const hedgeFundRequired = this.project.office.account.parentAccount.hedgeFundRequired;
      const {
        HEDGE_FUND
      } = this.staticData.CONST.ACCOUNT_TYPES;

      if (parentAccountType === HEDGE_FUND && hedgeFundRequired && isSlotAfterHedgeFundCompianceExpiration(newAvailabilityData, this.epl, this.staticData)) {
        throw new Error('Cannot schedule interview because the expert has no valid HF compliance after the schedule date!');
      }

      if (doesSlotOverlapWithAnySchedules(this.schedulesOnSameProject, newAvailabilityData)) {
        throw new Error(`The availability you're trying to add has some already taken slots.`);
      }

      const splitAvailabilitiesData = splitInto15MinAvailabilities(newAvailabilityData);
      const availabilities = splitAvailabilitiesData.map(a => new Availability({ ...a,
        store: this,
        new: true
      }));

      if (newAvailabilityData.type === 'CLIENT') {
        this.clientAvailabilities.replace(this.clientAvailabilities.concat(availabilities));
        eplApi.saveScheduleSettings(this.epl.id, {
          clientAvailabilities: this.cleanUpAvailabilities(availabilities)
        });
      } else {
        this.expertAvailabilities.replace(this.expertAvailabilities.concat(availabilities));
        eplApi.saveScheduleSettings(this.epl.id, {
          expertAvailabilities: this.cleanUpAvailabilities(availabilities)
        });
      }

      this.generateAvailabilityGroups();
    }));

    _defineProperty(this, "setSchedule", action(slot => {
      const EPL_STATUSES = this.staticData.CONST.EPL_STATUSES;

      if (this.epl.eplStatusId < EPL_STATUSES.SUBMITTED) {
        throw new Error('Cannot Schedule EPL in status lower than Submitted!');
      }

      this.overwriteSchedule = true;

      if (slot.schedule) {
        this.schedule = null;
        this.generateAvailabilityGroups();
        return;
      }

      const clientAvailabilities = slot.availabilities.filter(a => a.type === AVAILABILITY_TYPES.BOTH || a.type === AVAILABILITY_TYPES.CLIENT).map(a => ({
        id: a.clientAvailabilityId,
        startTime: a.startTime,
        endTime: a.endTime
      }));
      const expertAvailabilities = slot.availabilities.filter(a => a.type === AVAILABILITY_TYPES.BOTH || a.type === AVAILABILITY_TYPES.EXPERT).map(a => ({
        id: a.expertAvailabilityId,
        startTime: a.startTime,
        endTime: a.endTime
      }));
      const scheduleData = {
        startTime: slot.startTime,
        endTime: slot.endTime,
        duration: moment(slot.endTime).diff(slot.startTime, 'minutes'),
        clientAvailabilities,
        expertAvailabilities
      };
      this.schedule = scheduleData;
      this.generateAvailabilityGroups();
    }));

    _defineProperty(this, "saveSchedule", action(() => {
      this.submitting = true;
      const {
        overwriteSchedule,
        interviewDuration,
        lockInterviewDuration,
        schedule
      } = this;
      const {
        clientAvailabilities,
        expertAvailabilities
      } = this.filterOutAvailabilities();
      const eplScheduleData = {
        overwriteSchedule,
        schedule: overwriteSchedule ? toJS(schedule) : null,
        interviewDuration,
        lockInterviewDuration,
        clientAvailabilities: this.cleanUpAvailabilities(clientAvailabilities),
        expertAvailabilities: this.cleanUpAvailabilities(expertAvailabilities),
        expertPhoneNumbers: this.expertPhoneNumbers,
        sendSmsToPhoneNumberIds: this.sendSmsToPhoneNumberIds,
        sendSmsExpertPhoneNumber: this.sendSmsExpertPhoneNumber
      };
      return eplApi.saveScheduleSettings(this.epl.id, eplScheduleData).then(scheduleData => {
        this.overwriteSchedule = false;
        return scheduleData;
      });
    }));

    _defineProperty(this, "cleanUpAvailabilities", availabilites => {
      if (!availabilites || !availabilites.length) return availabilites;
      return availabilites.map(a => a.toJS());
    });

    _defineProperty(this, "filterOutAvailabilities", () => {
      const {
        schedule,
        clientAvailabilities,
        expertAvailabilities
      } = this;
      const newClientAvailabilities = clientAvailabilities.filter(a => a.new).filter(a => !schedule || schedule && !schedule.clientAvailabilities.includes(a));
      const newExpertAvailabilities = expertAvailabilities.filter(a => a.new).filter(a => !schedule || schedule && !schedule.expertAvailabilities.includes(a));
      return {
        clientAvailabilities: newClientAvailabilities,
        expertAvailabilities: newExpertAvailabilities
      };
    });

    this.epl = epl;
    this.project = project;
    this.staticData = staticData;
    this.userStore = userStore;
    this.availabilities = [];
    this.clientAvailabilities = null;
    this.expertAvailabilities = null;
    this.timezoneName = null;
    this.schedulesOnSameProject = null;
    this.scheduleRequests = null;
    this.expertPhoneNumbers = null;
    this.sendSmsToPhoneNumberIds = null;
    this.sendSmsExpertPhoneNumber = null;
    this.takenSlots = null;
    const {
      phoneTypeId: expertPhoneTypeId,
      phoneNum: expertPhoneNum
    } = this.epl.expert.expert;
    const {
      PHONE_TYPES
    } = staticData.CONST;
    const expertHasNumber = expertPhoneTypeId === PHONE_TYPES.MOBILE && expertPhoneNum;
    const sendSmsReminders = get(this.epl, 'expert.expert.sendSmsReminders', false);
    const expertPhoneNumbers = get(this.epl, 'expert.address.phones', []).filter(phone => phone.phoneTypeId === PHONE_TYPES.MOBILE);
    let sendSmsExpertPhoneNumber = null;
    let sendSmsToPhoneNumberIds = get(this.epl, 'phoneToAddressEpls', []).map(({
      id
    }) => id); // default check for expert number or expert numbers from internal app

    if (sendSmsReminders) {
      if (expertHasNumber) {
        sendSmsExpertPhoneNumber = this.epl.sendSmsExpertPhoneNumber === null ? true : Boolean(this.epl.sendSmsExpertPhoneNumber);
      } else {
        if (expertPhoneNumbers.length && !sendSmsToPhoneNumberIds.length) {
          sendSmsToPhoneNumberIds.push(expertPhoneNumbers[0].id);
        }
      }
    }

    const _EPL_STATUSES = staticData.CONST.EPL_STATUSES;
    const timezones = staticData.timezones.slice().sort((t1, t2) => moment.tz(t1.name).utcOffset() - moment.tz(t2.name).utcOffset());
    const currentTimezone = getTimezone();

    const _timezone = timezones.find(tz => tz.name === currentTimezone);

    extendObservable(this, {
      loading: false,
      error: null,
      submitting: false,
      isLoading: computed(() => this.loading || this.submitting),
      clientSchedulingRequest: epl.clientSchedulingRequest,
      clientAvailabilities: [],
      expertAvailabilities: [],
      takenSlots: [],
      availabilities: computed(() => {
        return this.clientAvailabilities.concat(this.expertAvailabilities);
      }),
      availabilityGroups: {},
      scheduleRequests: [],
      schedulesOnSameProject: [],
      // schedules on other EPLs but on same project
      schedule: null,
      persistedSchedule: null,
      persistedScheduleInfo: null,
      // will contain extra schedule info including zoom & outlook meeting
      // set if there's already an existing schedule which needs to be removed,
      // this will move the EPL to Not interviewed status if no new schedule is created
      overwriteSchedule: false,
      shouldUpdateEplToNotInterviewed: computed(() => {
        if (!this.persistedSchedule) return false;
        if (!this.schedule) return true;
        if (this.persistedSchedule.id !== this.schedule.id) return true;
        return false;
      }),
      interviewDuration: this.epl.interviewDuration || 60,
      lockInterviewDuration: this.epl.lockInterviewDuration || false,
      // set to disable interview duration changing on client interface
      isInterviewDurationLocked: computed(() => {
        return false && this.epl.eplStatusId >= _EPL_STATUSES.SCHEDULED;
      }),
      interviewDurationOptions: [{
        value: 30,
        label: '30 mins'
      }, {
        value: 60,
        label: '60 mins'
      }, {
        value: 90,
        label: '90 mins'
      }, {
        value: 120,
        label: '120 mins'
      }],
      officeTimezone: null,
      address: null,
      timezoneOptions: computed(() => {
        const relevantTimezones = [];
        const browserTimezoneName = getTimezone();
        let browserTimezone = timezones.find(tz => tz.name === browserTimezoneName);

        if (browserTimezone) {
          relevantTimezones.push({
            label: getFormattedTimezone(browserTimezone.name) + ' (Current)',
            value: browserTimezone.id
          });
        } else {
          const timezoneId = this.address && this.address.timezoneId;

          if (timezoneId) {
            const userTimezone = timezones.find(tz => tz.id === timezoneId);
            relevantTimezones.push({
              label: getFormattedTimezone(userTimezone.name) + ' (Current)',
              value: userTimezone.id
            });
          }
        }

        const expertTimezoneId = get(this.epl, 'expert.address.timezoneId');
        const expertTimezone = timezones.find(tz => tz.id === expertTimezoneId);

        if (expertTimezone) {
          relevantTimezones.push({
            label: getFormattedTimezone(expertTimezone.name) + ' (Expert)',
            value: expertTimezone.id
          });
        }

        if (this.officeTimezone) {
          relevantTimezones.push({
            label: `${getFormattedTimezone(this.officeTimezone.name)} (Client)`,
            value: this.officeTimezone.id
          });
        }

        const allTimezones = timezones.map(timezone => {
          return {
            label: getFormattedTimezone(timezone.name),
            value: timezone.id
          };
        });
        const options = [...relevantTimezones, ...allTimezones];
        return options;
      }),
      timezone: _timezone && _timezone.id,
      timezoneName: computed(() => {
        const timezoneId = this.timezone;
        const timezone = timezones.find(tz => tz.id === timezoneId);
        if (timezone) return timezone.name;
      }),
      formattedTimezoneName: computed(() => {
        if (this.timezoneName) return getFormattedTimezone(this.timezoneName);
        return '';
      }),
      sendSmsToPhoneNumberIds,
      sendSmsExpertPhoneNumber,
      expertHasNumber,
      expertPhoneNum: get(this.epl, 'expert.expert.phoneNum', ''),
      sendSmsReminders,
      expertPhoneNumbers
    });
  }

}

export default AvailabilityStore;